import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import ClientProfileDrawer from '../components/ClientProfileDrawer';
import api from '../api/axios';
import { C } from '../theme';

const BLANK = {
  client_id:    '',
  auto_id:      true,
  client_name:  '',
  purpose:      'own',
  company_name: '',
  gst_number:   '',
  contact:      '',
  email:        '',
  address:      '',
};

export default function Clients() {
  const [activeTab, setActiveTab] = useState('admin'); // 'admin' | 'dealer'

  // Admin clients
  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  // Dealer clients
  const [dealerClients,  setDealerClients]  = useState([]);
  const [dealerLoading,  setDealerLoading]  = useState(false);
  const [dealerSearch,   setDealerSearch]   = useState('');

  // Filters
  const [search,      setSearch]      = useState('');
  const [purposeFilter, setPurposeFilter] = useState('');
  const [stFilter,    setStFilter]    = useState('');

  // Modal
  const [modal,   setModal]   = useState(null);   // 'add'|'edit'|'delete'
  const [selClt,  setSelClt]  = useState(null);
  const [form,    setForm]    = useState(BLANK);
  const [saving,  setSaving]  = useState(false);
  const [errs,    setErrs]    = useState({});

  const [toast, setToast] = useState(null);

  // Bottom slide-up profile drawer
  const [profileId, setProfileId] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/clients?dealer_source=admin');
      setClients(r.data);
    } finally { setLoading(false); }
  }, []);

  const loadDealerClients = useCallback(async () => {
    setDealerLoading(true);
    try {
      const r = await api.get('/api/clients?dealer_source=dealer');
      setDealerClients(r.data);
    } finally { setDealerLoading(false); }
  }, []);

  useEffect(() => { loadAll(); loadDealerClients(); }, [loadAll, loadDealerClients]);

  // ── CRUD ──────────────────────────────────────────────────────────────────

  const openAdd = () => {
    setForm(BLANK); setErrs({}); setSelClt(null); setModal('add');
  };
  const openEdit = (c) => {
    setForm({
      client_id:    c.client_id || '',
      auto_id:      false,
      client_name:  c.client_name || '',
      purpose:      c.purpose || 'own',
      company_name: c.company_name || '',
      gst_number:   c.gst_number || '',
      contact:      c.contact || '',
      email:        c.email || '',
      address:      c.address || '',
    });
    setErrs({}); setSelClt(c); setModal('edit');
  };
  const openDelete = (c) => { setSelClt(c); setModal('delete'); };
  const closeModal = () => { setModal(null); setSelClt(null); setErrs({}); };

  const f = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrs(p => ({ ...p, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.client_name.trim())       e.client_name  = 'Client name is required.';
    if (!form.contact.trim())           e.contact      = 'Contact number is required.';
    if (!form.auto_id && !form.client_id.trim()) e.client_id = 'Enter a Client ID or enable auto-generate.';
    if (form.purpose === 'company' && !form.company_name.trim()) e.company_name = 'Company name is required.';
    return e;
  };

  const save = async () => {
    const e = validate();
    if (Object.keys(e).length) return setErrs(e);
    setSaving(true);
    try {
      const body = {
        client_id:    form.auto_id ? null : form.client_id.trim(),
        client_name:  form.client_name.trim(),
        purpose:      form.purpose,
        company_name: form.purpose === 'company' ? form.company_name.trim() : null,
        gst_number:   form.purpose === 'company' ? (form.gst_number.trim() || null) : null,
        contact:      form.contact.trim(),
        email:        form.email.trim() || null,
        address:      form.address.trim() || null,
      };
      if (selClt) await api.put(`/api/clients/${selClt.id}`, body);
      else         await api.post('/api/clients', body);
      await loadAll();
      showToast('success', selClt ? 'Client updated.' : 'Client added.');
      closeModal();
    } catch (ex) {
      showToast('error', ex?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/clients/${selClt.id}`);
      await loadAll();
      showToast('success', 'Client deleted.');
      closeModal();
    } catch (ex) {
      showToast('error', ex?.response?.data?.detail || 'Delete failed.');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (c) => {
    const next = c.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/api/clients/${c.id}`, { status: next });
      setClients(prev => prev.map(x => x.id === c.id ? { ...x, status: next } : x));
    } catch { showToast('error', 'Status update failed.'); }
  };

  // ── Filters ───────────────────────────────────────────────────────────────

  const filtered = clients.filter(c => {
    const q = search.toLowerCase();
    return (
      (!q || c.client_name?.toLowerCase().includes(q) || c.client_id?.toLowerCase().includes(q)
        || c.contact?.includes(q) || c.company_name?.toLowerCase().includes(q)
        || c.email?.toLowerCase().includes(q))
      && (!purposeFilter || c.purpose === purposeFilter)
      && (!stFilter      || c.status  === stFilter)
    );
  });

  // Dealer clients filter
  const filteredDealer = dealerClients.filter(c => {
    const q = dealerSearch.toLowerCase();
    return !q || c.client_name?.toLowerCase().includes(q) || c.client_id?.toLowerCase().includes(q)
      || c.contact?.includes(q) || c.dealer_name?.toLowerCase().includes(q);
  });

  return (
    <Layout title="Clients">

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '22px', borderBottom: `2px solid ${C.border}`, paddingBottom: '0' }}>
        {[
          { key: 'admin',  label: '🏢 My Clients',     count: clients.length },
          { key: 'dealer', label: '🏪 Dealer Clients',  count: dealerClients.length },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)} style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer', fontWeight: 700,
            fontSize: '13px', borderRadius: '8px 8px 0 0', marginBottom: '-2px',
            background: activeTab === t.key ? C.white : 'transparent',
            color: activeTab === t.key ? C.primary : C.textSub,
            borderBottom: activeTab === t.key ? `2px solid ${C.primary}` : '2px solid transparent',
            transition: 'all 0.15s',
          }}>
            {t.label}
            <span style={{
              marginLeft: '8px', fontSize: '11px', fontWeight: 700,
              background: activeTab === t.key ? C.primaryLight : '#F3F4F6',
              color: activeTab === t.key ? C.primaryDeep : C.textSub,
              padding: '1px 7px', borderRadius: '10px',
            }}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* ── Admin Clients Tab ── */}
      {activeTab === 'admin' && (<>
        {/* Summary */}
        <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { label: 'Total Clients', val: clients.length,                                           dot: C.primary },
            { label: 'Own',           val: clients.filter(c => c.purpose === 'own').length,          dot: C.info    },
            { label: 'Company',       val: clients.filter(c => c.purpose === 'company').length,      dot: C.warning },
            { label: 'Active',        val: clients.filter(c => c.status  === 'active').length,       dot: C.success },
          ].map(({ label, val, dot }) => (
            <div key={label} style={{
              background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px',
              padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px',
            }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
              <div>
                <div style={{ fontSize: '22px', fontWeight: 800, color: C.textMain }}>{val}</div>
                <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
              </div>
            </div>
          ))}
          <button onClick={openAdd} style={{ ...btnPrimary, marginLeft: 'auto' }}>
            + Add Client
          </button>
        </div>

        {/* Table card */}
        <div style={card}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              placeholder="Search name, ID, contact, email…"
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: '200px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }}
            />
            <select value={purposeFilter} onChange={e => setPurposeFilter(e.target.value)} style={selStyle}>
              <option value="">All Types</option>
              <option value="own">Own</option>
              <option value="company">Company</option>
            </select>
            <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={selStyle}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            {(search || purposeFilter || stFilter) && (
              <button onClick={() => { setSearch(''); setPurposeFilter(''); setStFilter(''); }} style={btnGhost}>Clear</button>
            )}
            <span style={{ fontSize: '12px', color: C.textSub }}>{filtered.length} / {clients.length}</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '780px' }}>
              <thead>
                <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                  {['Client', 'Type', 'Company / GST', 'Contact', 'Status', 'Actions'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={emptyCell}>Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} style={emptyCell}>
                    {clients.length === 0 ? 'No clients yet. Click "+ Add Client" to start.' : 'No results match the filters.'}
                  </td></tr>
                ) : filtered.map((c, i) => (
                  <tr key={c.id}
                    onClick={() => setProfileId(c.id)}
                    title="View full client profile"
                    style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = C.primaryBg; }}
                    onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? C.white : '#FFFBF5'; }}
                  >

                    {/* Client */}
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0,
                          background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '15px', fontWeight: 800, color: '#fff',
                        }}>
                          {(c.client_name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: C.textMain, fontSize: '13px' }}>{c.client_name}</div>
                          <div style={{ fontSize: '11px', color: C.primaryDeep, fontFamily: 'monospace', fontWeight: 600 }}>{c.client_id}</div>
                        </div>
                      </div>
                    </td>

                    {/* Type */}
                    <td style={td}>
                      <span style={{
                        padding: '3px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                        background: c.purpose === 'company' ? C.warningBg : C.infoBg,
                        color:      c.purpose === 'company' ? C.warningText : C.infoText,
                      }}>
                        {c.purpose === 'company' ? '🏢 Company' : '👤 Own'}
                      </span>
                    </td>

                    {/* Company / GST */}
                    <td style={td}>
                      {c.purpose === 'company' ? (
                        <div>
                          <div style={{ fontWeight: 600, color: C.textMain, fontSize: '12px' }}>{c.company_name || '—'}</div>
                          {c.gst_number && (
                            <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>GST: {c.gst_number}</div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: C.textSub, fontSize: '12px' }}>—</span>
                      )}
                    </td>

                    {/* Contact */}
                    <td style={td}>
                      <div style={{ fontSize: '13px', color: C.textMain }}>{c.contact}</div>
                      {c.email && <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>{c.email}</div>}
                      {c.address && <div style={{ fontSize: '11px', color: C.textSub, marginTop: '1px' }}>📍 {c.address.length > 35 ? c.address.slice(0, 35) + '…' : c.address}</div>}
                    </td>

                    {/* Status */}
                    <td style={td} onClick={e => e.stopPropagation()}>
                      <button onClick={() => toggleStatus(c)} style={{
                        border: 'none', cursor: 'pointer', borderRadius: '20px',
                        padding: '5px 14px', fontSize: '11px', fontWeight: 600,
                        background: c.status === 'active' ? C.successBg : C.dangerBg,
                        color:      c.status === 'active' ? C.successText : C.dangerText,
                      }}>
                        {c.status === 'active' ? '● Active' : '○ Inactive'}
                      </button>
                    </td>

                    {/* Actions */}
                    <td style={td} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <SmBtn label="View"   color={C.primaryDeep} onClick={() => setProfileId(c.id)} />
                        <SmBtn label="Edit"   color="#3B82F6" onClick={() => openEdit(c)} />
                        <SmBtn label="Delete" color={C.danger} onClick={() => openDelete(c)} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>)}

      {/* ── Dealer Clients Tab ── */}
      {activeTab === 'dealer' && (
        <div style={card}>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              placeholder="Search by name, ID, contact, dealer…"
              value={dealerSearch} onChange={e => setDealerSearch(e.target.value)}
              style={{ flex: 1, minWidth: '200px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }}
            />
            {dealerSearch && (
              <button onClick={() => setDealerSearch('')} style={btnGhost}>Clear</button>
            )}
            <span style={{ fontSize: '12px', color: C.textSub }}>{filteredDealer.length} / {dealerClients.length}</span>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
              <thead>
                <tr style={{ background: '#F5F3FF', borderBottom: `2px solid ${C.border}` }}>
                  {['Client', 'Type', 'Contact', 'Dealer', 'Status', 'Added'].map(h => (
                    <th key={h} style={th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dealerLoading ? (
                  <tr><td colSpan={6} style={emptyCell}>Loading…</td></tr>
                ) : filteredDealer.length === 0 ? (
                  <tr><td colSpan={6} style={emptyCell}>
                    {dealerClients.length === 0 ? 'No dealer clients yet.' : 'No results match the search.'}
                  </td></tr>
                ) : filteredDealer.map((c, i) => (
                  <tr key={c.id}
                    onClick={() => setProfileId(c.id)}
                    title="View full client profile"
                    style={{ background: i % 2 === 0 ? C.white : '#FDFAFF', borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#F5F3FF'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = i % 2 === 0 ? C.white : '#FDFAFF'; }}
                  >

                    {/* Client */}
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                          background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '14px', fontWeight: 800, color: '#fff',
                        }}>
                          {(c.client_name || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: C.textMain, fontSize: '13px' }}>{c.client_name}</div>
                          <div style={{ fontSize: '11px', color: '#7C3AED', fontFamily: 'monospace', fontWeight: 600 }}>{c.client_id}</div>
                        </div>
                      </div>
                    </td>

                    {/* Type */}
                    <td style={td}>
                      <span style={{
                        padding: '3px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                        background: c.purpose === 'company' ? C.warningBg : C.infoBg,
                        color:      c.purpose === 'company' ? C.warningText : C.infoText,
                      }}>
                        {c.purpose === 'company' ? '🏢 Company' : '👤 Own'}
                      </span>
                    </td>

                    {/* Contact */}
                    <td style={td}>
                      <div style={{ fontSize: '13px', color: C.textMain }}>{c.contact || '—'}</div>
                      {c.email && <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>{c.email}</div>}
                    </td>

                    {/* Dealer */}
                    <td style={td}>
                      <span style={{
                        padding: '3px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                        background: '#F5F3FF', color: '#6D28D9',
                        border: '1px solid #DDD6FE',
                      }}>
                        🏪 {c.dealer_name || '—'}
                      </span>
                    </td>

                    {/* Status */}
                    <td style={td}>
                      <span style={{
                        padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                        background: c.status === 'active' ? C.successBg : C.dangerBg,
                        color:      c.status === 'active' ? C.successText : C.dangerText,
                      }}>
                        {c.status === 'active' ? '● Active' : '○ Inactive'}
                      </span>
                    </td>

                    {/* Added */}
                    <td style={td}>
                      <span style={{ fontSize: '12px', color: C.textSub }}>
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : '—'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      {(modal === 'add' || modal === 'edit') && (
        <Overlay>
          <div style={modalBox}>
            <MHeader
              title={modal === 'add' ? 'Add Client' : `Edit — ${selClt?.client_name}`}
              onClose={closeModal}
            />
            <div style={{ padding: '24px 28px', overflowY: 'auto', maxHeight: '72vh' }}>

              {/* Client ID row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '4px' }}>
                <div>
                  <FF label="Client ID *" error={errs.client_id}>
                    <input
                      value={form.client_id}
                      onChange={e => f('client_id', e.target.value)}
                      disabled={form.auto_id}
                      placeholder="Enter Client ID"
                      style={{ ...inputBase, borderColor: errs.client_id ? C.danger : C.border, background: form.auto_id ? '#F9FAFB' : C.white, color: form.auto_id ? C.textSub : C.textMain }}
                    />
                  </FF>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.textSub, marginTop: '6px', cursor: 'pointer', userSelect: 'none' }}>
                    <input type="checkbox" checked={form.auto_id} onChange={e => f('auto_id', e.target.checked)}
                      style={{ accentColor: C.primary, width: '14px', height: '14px' }} />
                    Auto Generate Client ID
                  </label>
                </div>
                <FF label="Client Name *" error={errs.client_name}>
                  <input value={form.client_name} onChange={e => f('client_name', e.target.value)}
                    placeholder="Enter Client Name" style={{ ...inputBase, borderColor: errs.client_name ? C.danger : C.border }} />
                </FF>
              </div>

              {/* Purpose selector */}
              <div style={{ marginBottom: '16px', marginTop: '8px' }}>
                <label style={labelSt}>Purpose *</label>
                <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                  {[
                    { val: 'own',     label: '👤 Own',     desc: 'Individual / personal client' },
                    { val: 'company', label: '🏢 Company', desc: 'Business / corporate client'  },
                  ].map(opt => (
                    <button key={opt.val} onClick={() => f('purpose', opt.val)} style={{
                      flex: 1, padding: '12px 16px', borderRadius: '10px', cursor: 'pointer',
                      textAlign: 'left',
                      border: `2px solid ${form.purpose === opt.val ? C.primary : C.border}`,
                      background: form.purpose === opt.val ? C.primaryLight : C.white,
                      transition: 'all 0.15s',
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: form.purpose === opt.val ? C.primaryDeep : C.textMain }}>
                        {opt.label}
                      </div>
                      <div style={{ fontSize: '11px', color: C.textSub, marginTop: '3px' }}>{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Company fields — only when purpose = company */}
              {form.purpose === 'company' && (
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px',
                  padding: '16px', background: C.warningBg, borderRadius: '10px',
                  border: `1px solid ${C.warning}40`, marginBottom: '16px',
                }}>
                  <FF label="Company Name *" error={errs.company_name}>
                    <input value={form.company_name} onChange={e => f('company_name', e.target.value)}
                      placeholder="Enter Company Name" style={{ ...inputBase, borderColor: errs.company_name ? C.danger : C.border }} />
                  </FF>
                  <FF label="GST Number">
                    <input value={form.gst_number} onChange={e => f('gst_number', e.target.value)}
                      placeholder="Enter GST Number" style={{ ...inputBase, borderColor: C.border }} />
                  </FF>
                </div>
              )}

              {/* Contact + Email */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <FF label="Contact Number *" error={errs.contact}>
                  <input value={form.contact} onChange={e => f('contact', e.target.value)}
                    placeholder="Enter Contact Number" style={{ ...inputBase, borderColor: errs.contact ? C.danger : C.border }} />
                </FF>
                <FF label="Email Address">
                  <input type="email" value={form.email} onChange={e => f('email', e.target.value)}
                    placeholder="Enter Email Address" style={{ ...inputBase, borderColor: C.border }} />
                </FF>
              </div>

              {/* Address */}
              <div style={{ marginTop: '4px' }}>
                <FF label="Address">
                  <textarea value={form.address} onChange={e => f('address', e.target.value)}
                    placeholder="Enter full address…" rows={3}
                    style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
                </FF>
              </div>
            </div>
            <MFooter onClose={closeModal} onSave={save} saving={saving}
              label={modal === 'add' ? '+ Add Client' : '💾 Update Client'} />
          </div>
        </Overlay>
      )}

      {/* Delete modal */}
      {modal === 'delete' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <MHeader title="Delete Client" onClose={closeModal} />
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: '13px', color: C.textMain, lineHeight: 1.6, margin: 0 }}>
                Delete <strong style={{ color: C.danger }}>{selClt?.client_name}</strong>{' '}
                <span style={{ fontSize: '11px', color: C.textSub }}>({selClt?.client_id})</span>?
                <br /><span style={{ fontSize: '12px', color: C.textSub }}>This cannot be undone.</span>
              </p>
            </div>
            <MFooter onClose={closeModal} onSave={confirmDelete} saving={saving} label="Delete" danger />
          </div>
        </Overlay>
      )}

      {/* Bottom slide-up client profile */}
      {profileId && (
        <ClientProfileDrawer clientId={profileId} onClose={() => setProfileId(null)} />
      )}
    </Layout>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SmBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
      background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer',
    }}>{label}</button>
  );
}

function FF({ label, error, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={labelSt}>{label}</label>
      <div style={{ marginTop: '6px' }}>{children}</div>
      {error && <div style={{ fontSize: '11px', color: C.danger, marginTop: '4px' }}>⚠ {error}</div>}
    </div>
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}>{children}</div>
  );
}

function MHeader({ title, onClose }) {
  return (
    <div style={{
      padding: '22px 28px', borderBottom: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
        <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
    </div>
  );
}

function MFooter({ onClose, onSave, saving, label, danger = false }) {
  return (
    <div style={{
      padding: '16px 28px', borderTop: `1px solid ${C.border}`,
      display: 'flex', justifyContent: 'flex-end', gap: '10px',
    }}>
      <button onClick={onClose} style={btnGhost}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={danger ? btnDanger : btnPrimary}>
        {saving ? 'Saving…' : label}
      </button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const labelSt   = { display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain };
const inputBase = { width: '100%', padding: '9px 12px', border: '1.5px solid', borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none', boxSizing: 'border-box' };
const card      = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const modalBox  = { background: C.white, borderRadius: '16px', width: '100%', maxWidth: '620px', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}` };
const th        = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td        = { padding: '13px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const selStyle  = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary = {
  padding: '10px 20px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
  color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700,
  cursor: 'pointer', boxShadow: `0 3px 10px rgba(245,166,35,0.3)`,
};
const btnGhost  = { padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
const btnDanger = { padding: '10px 18px', background: C.danger, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
